/**
 * EmployerReviewsPage.jsx — Phase 1.0 reviewer queue + decision UX.
 *
 * Surfaces:
 *   - "My reviews" queue scoped to the JWT user via /api/employer/v1/reviewer/queue.
 *   - Inline lifecycle controls per row: Start → Decide (advance/reject/on_hold) + Add note.
 *   - Slice B X-Request-Id is echoed in error toasts via `apiFetch`.
 *   - Slice C idempotency is enabled by sending an `Idempotency-Key` on every write.
 *
 * Out of scope (per Phase 1.0 PRE-CODE plan):
 *   - admin reviewer-roster panel (deferred follow-up)
 *   - email notifications, SLA timers (Phase 1.1)
 *   - candidate-facing surfaces
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Inbox, CheckCircle2, XCircle, PauseCircle, Play, MessageSquarePlus } from 'lucide-react';

import {
  fetchReviewerQueue, startReview, decideReview, addReviewNote,
} from '@/lib/employerReviews';
import { t } from '@/lib/employerReviewerStrings';
import HelpOrientationCard from '@/components/HelpOrientationCard';

const STATUS_LABEL = {
  pending:   { key: 'reviewer.status.pending',   variant: 'secondary' },
  in_review: { key: 'reviewer.status.in_review', variant: 'default' },
  decided:   { key: 'reviewer.status.decided',   variant: 'outline' },
};

const DECISION_ICON = {
  advance: <CheckCircle2 className="h-4 w-4 text-emerald-600" />,
  reject:  <XCircle className="h-4 w-4 text-rose-600" />,
  on_hold: <PauseCircle className="h-4 w-4 text-amber-600" />,
};

function genIdempotencyKey() {
  return `rev-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export default function EmployerReviewsPage() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId]   = useState(null);
  const [decideRow, setDecideRow] = useState(null);
  const [noteRow,   setNoteRow]   = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchReviewerQueue({ limit: 100 });
      setRows(data?.reviews || []);
    } catch (e) {
      toast.error(e?.message || t('reviewer.toast.queue.fail'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const handleStart = useCallback(async (row) => {
    setBusyId(row.review_id);
    try {
      await startReview({ reviewId: row.review_id, idempotencyKey: genIdempotencyKey() });
      toast.success(t('reviewer.toast.start.success'));
      reload();
    } catch (e) {
      toast.error(e?.message || t('reviewer.toast.start.fail'));
    } finally {
      setBusyId(null);
    }
  }, [reload]);

  const counts = useMemo(() => ({
    pending:   rows.filter(r => r.status === 'pending').length,
    inReview:  rows.filter(r => r.status === 'in_review').length,
    decided:   rows.filter(r => r.status === 'decided').length,
  }), [rows]);

  return (
    <div className="space-y-6 p-6" data-testid="reviewer-queue-page">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">{t('reviewer.queue.title')}</h1>
        <p className="text-sm text-muted-foreground">
          {t('reviewer.queue.subtitle')}
        </p>
      </header>

      <div className="grid grid-cols-3 gap-3" data-testid="reviewer-queue-counters">
        <Card><CardContent className="p-4">
          <div className="text-xs uppercase text-muted-foreground">{t('reviewer.queue.counter.pending')}</div>
          <div className="text-2xl font-semibold">{counts.pending}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs uppercase text-muted-foreground">{t('reviewer.queue.counter.in_review')}</div>
          <div className="text-2xl font-semibold">{counts.inReview}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs uppercase text-muted-foreground">{t('reviewer.queue.counter.decided')}</div>
          <div className="text-2xl font-semibold">{counts.decided}</div>
        </CardContent></Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Inbox className="h-4 w-4" /> {t('reviewer.queue.section.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> {t('reviewer.queue.loading')}
            </div>
          ) : rows.length === 0 ? (
            <div
              data-testid="reviewer-queue-empty"
              className="py-10 text-center space-y-3 max-w-md mx-auto"
            >
              <Inbox className="mx-auto h-8 w-8 text-muted-foreground/70" />
              <p className="text-sm font-medium text-foreground">
                {t('reviewer.queue.empty')}
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {t('reviewer.queue.empty.body')}
              </p>
              <Button
                asChild
                variant="outline"
                size="sm"
                data-testid="reviewer-queue-empty-cta"
              >
                <a href="/employer">{t('reviewer.queue.empty.cta')}</a>
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="reviewer-queue-table">
                <thead>
                  <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                    <th className="py-2 pr-3">{t('reviewer.queue.col.review')}</th>
                    <th className="py-2 pr-3">{t('reviewer.queue.col.status')}</th>
                    <th className="py-2 pr-3">{t('reviewer.queue.col.decision')}</th>
                    <th className="py-2 pr-3">{t('reviewer.queue.col.routed')}</th>
                    <th className="py-2 pr-3">{t('reviewer.queue.col.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const status = STATUS_LABEL[r.status] || { label: r.status, variant: 'outline' };
                    return (
                      <tr key={r.review_id} className="border-b last:border-0" data-testid={`reviewer-row-${r.review_id}`}>
                        <td className="py-2 pr-3 font-mono text-xs">{r.review_id.slice(0, 8)}…</td>
                        <td className="py-2 pr-3">
                          <Badge variant={status.variant}>{t(status.key)}</Badge>
                        </td>
                        <td className="py-2 pr-3">
                          {r.decision ? (
                            <span className="inline-flex items-center gap-1">
                              {DECISION_ICON[r.decision] || null}
                              <span className="capitalize">{r.decision.replace('_', ' ')}</span>
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-xs text-muted-foreground">
                          {new Date(r.routed_at).toLocaleString()}
                        </td>
                        <td className="py-2 pr-3">
                          <div className="flex flex-wrap gap-2">
                            <Button
                              size="sm" variant="ghost"
                              data-testid={`reviewer-open-${r.review_id}`}
                              asChild
                            >
                              <a href={`/employer/reviews/${r.review_id}`}>Open</a>
                            </Button>
                            {r.status === 'pending' && (
                              <Button
                                size="sm" variant="secondary"
                                data-testid={`reviewer-start-${r.review_id}`}
                                disabled={busyId === r.review_id}
                                onClick={() => handleStart(r)}
                              >
                                <Play className="mr-1 h-3 w-3" /> {t('reviewer.queue.action.start')}
                              </Button>
                            )}
                            {r.status === 'in_review' && (
                              <Button
                                size="sm"
                                data-testid={`reviewer-decide-open-${r.review_id}`}
                                onClick={() => setDecideRow(r)}
                              >
                                {t('reviewer.queue.action.decide')}
                              </Button>
                            )}
                            <Button
                              size="sm" variant="outline"
                              data-testid={`reviewer-note-open-${r.review_id}`}
                              onClick={() => setNoteRow(r)}
                            >
                              <MessageSquarePlus className="mr-1 h-3 w-3" /> {t('reviewer.queue.action.note')}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <DecideDialog row={decideRow} onClose={() => setDecideRow(null)} onDone={reload} />
      <NoteDialog row={noteRow}   onClose={() => setNoteRow(null)}   onDone={reload} />

      {/* RC 1.63.1 — Help & orientation stub. Reviewers land here
          (not on /employer), so the stub has to render here too. We
          pass memberRole=null so the Employer User Guide row stays
          hidden even if a higher-authority member happens to open
          this page directly. The Reviewer User Guide + How It Works
          are visible to every employer-workspace role. */}
      <HelpOrientationCard workspace="employer" memberRole={null} />
    </div>
  );
}

export function DecideDialog({ row, onClose, onDone }) {
  const [decision, setDecision]   = useState('advance');
  const [rationale, setRationale] = useState('');
  const [busy, setBusy]           = useState(false);
  const open = Boolean(row);

  useEffect(() => {
    if (row) { setDecision('advance'); setRationale(''); }
  }, [row]);

  const requireRationale = decision === 'reject';

  const submit = async () => {
    if (!row) return;
    if (requireRationale && !rationale.trim()) {
      toast.error(t('reviewer.decide.rationale.warning'));
      return;
    }
    setBusy(true);
    try {
      await decideReview({
        reviewId:         row.review_id,
        decision,
        rationaleExcerpt: rationale.trim() || null,
        idempotencyKey:   genIdempotencyKey(),
      });
      toast.success(t('reviewer.decide.toast.success'));
      onClose();
      onDone();
    } catch (e) {
      toast.error(e?.message || t('reviewer.decide.toast.fail'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="reviewer-decide-dialog">
        <DialogHeader>
          <DialogTitle>{t('reviewer.decide.title')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <div className="mb-1 text-sm font-medium">{t('reviewer.decide.field.decision')}</div>
            <Select value={decision} onValueChange={setDecision}>
              <SelectTrigger data-testid="reviewer-decision-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="advance">{t('reviewer.decide.option.advance')}</SelectItem>
                <SelectItem value="reject">{t('reviewer.decide.option.reject')}</SelectItem>
                <SelectItem value="on_hold">{t('reviewer.decide.option.on_hold')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="mb-1 text-sm font-medium">
              {t('reviewer.decide.field.rationale')} {requireRationale && <span className="text-rose-600">*</span>}
            </div>
            <Textarea
              data-testid="reviewer-rationale-textarea"
              maxLength={2000}
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              placeholder={requireRationale ? t('reviewer.decide.rationale.required') : t('reviewer.decide.rationale.optional')}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>{t('reviewer.decide.cancel')}</Button>
          <Button data-testid="reviewer-decide-submit" onClick={submit} disabled={busy}>
            {busy && <Loader2 className="mr-1 h-3 w-3 animate-spin" />} {t('reviewer.decide.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function NoteDialog({ row, onClose, onDone }) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const open = Boolean(row);

  useEffect(() => { if (row) setBody(''); }, [row]);

  const submit = async () => {
    if (!row || !body.trim()) return;
    setBusy(true);
    try {
      await addReviewNote({
        reviewId: row.review_id,
        body:     body.trim(),
        idempotencyKey: genIdempotencyKey(),
      });
      toast.success(t('reviewer.note.toast.success'));
      onClose();
      onDone();
    } catch (e) {
      toast.error(e?.message || t('reviewer.note.toast.fail'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="reviewer-note-dialog">
        <DialogHeader>
          <DialogTitle>{t('reviewer.note.title')}</DialogTitle>
        </DialogHeader>
        <Textarea
          data-testid="reviewer-note-textarea"
          maxLength={4000} rows={6}
          value={body} onChange={(e) => setBody(e.target.value)}
          placeholder={t('reviewer.note.placeholder')}
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>{t('reviewer.note.cancel')}</Button>
          <Button data-testid="reviewer-note-submit"
                  onClick={submit} disabled={busy || !body.trim()}>
            {busy && <Loader2 className="mr-1 h-3 w-3 animate-spin" />} {t('reviewer.note.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
